import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../styles/withdraw.css";

const NETWORK = "TRC20";
const TOKEN = "USDT";
const MIN_WITHDRAW = 100;
const TRANSACTION_FEE_PERCENT = 10;
const EARLY_WITHDRAW_FEE_PERCENT = 20;
const EARLY_WITHDRAW_DAYS = 30;
const API_BASE_URL = "https://growrise.shop";

export default function Withdraw() {
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  const getToken = () => {
    return (
      localStorage.getItem("token") || localStorage.getItem("naxoraToken") || ""
    );
  };

  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);

      const token = getToken();

      if (!token) {
        throw new Error("Login required");
      }

      const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.user) {
        throw new Error(data.message || "Unable to load profile");
      }

      setUserProfile(data.user);
    } catch (error) {
      console.error("Withdraw profile fetch error:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const numericAmount = Number(amount || 0);
  const safeAmount = Number.isNaN(numericAmount) ? 0 : numericAmount;

  const createdAt =
    userProfile?.createdAt ||
    userProfile?.registeredAt ||
    userProfile?.joinedAt ||
    userProfile?.dateCreated ||
    null;

  const earlyWithdrawApplies = useMemo(() => {
    if (!createdAt) {
      return true;
    }

    const joinDate = new Date(createdAt);
    const now = new Date();

    if (Number.isNaN(joinDate.getTime())) {
      return true;
    }

    const diffMs = now.getTime() - joinDate.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    return diffDays < EARLY_WITHDRAW_DAYS;
  }, [createdAt]);

  const transactionFee = useMemo(() => {
    return safeAmount > 0
      ? (safeAmount * TRANSACTION_FEE_PERCENT) / 100
      : 0;
  }, [safeAmount]);

  const earlyWithdrawFee = useMemo(() => {
    if (!earlyWithdrawApplies || safeAmount <= 0) {
      return 0;
    }

    return (safeAmount * EARLY_WITHDRAW_FEE_PERCENT) / 100;
  }, [safeAmount, earlyWithdrawApplies]);

  const totalFee = transactionFee + earlyWithdrawFee;

  const receivableAmount = useMemo(() => {
    const value = safeAmount - totalFee;
    return value > 0 ? value : 0;
  }, [safeAmount, totalFee]);

  const totalFeePercent = useMemo(() => {
    return TRANSACTION_FEE_PERCENT + (earlyWithdrawApplies ? EARLY_WITHDRAW_FEE_PERCENT : 0);
  }, [earlyWithdrawApplies]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!amount || Number.isNaN(numericAmount)) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Enter valid amount",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (safeAmount < MIN_WITHDRAW) {
      Swal.fire({
        icon: "warning",
        title: "Minimum Withdraw",
        text: `Minimum withdraw is $${MIN_WITHDRAW}`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (!address.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Address Required",
        text: "Enter wallet address",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    if (receivableAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Withdraw",
        text: "Receivable amount must be greater than zero",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
      return;
    }

    try {
      setSubmitting(true);

      const token = getToken();

      if (!token) {
        throw new Error("Login required");
      }

      const response = await fetch(`${API_BASE_URL}/api/withdrawals/request`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: Number(receivableAmount.toFixed(2)),
          method: NETWORK,
          walletAddress: address.trim(),
          note: `${TOKEN} withdraw request | Requested: ${safeAmount.toFixed(
            2
          )} | Transaction Fee: ${transactionFee.toFixed(
            2
          )} | Early Fee: ${earlyWithdrawFee.toFixed(
            2
          )} | Total Fee: ${totalFee.toFixed(
            2
          )} | Final Receivable: ${receivableAmount.toFixed(2)}`,
          requestedAmount: Number(safeAmount.toFixed(2)),
          transactionFee: Number(transactionFee.toFixed(2)),
          earlyWithdrawFee: Number(earlyWithdrawFee.toFixed(2)),
          totalFee: Number(totalFee.toFixed(2)),
          finalReceivable: Number(receivableAmount.toFixed(2)),
          isEarlyWithdraw: earlyWithdrawApplies,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Withdraw failed");
      }

      Swal.fire({
        icon: "success",
        title: "Requested",
        text: `Withdraw request sent for approval. Final receivable: $${receivableAmount.toFixed(
          2
        )}`,
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      setAmount("");
      setAddress("");
      fetchProfile();

      window.dispatchEvent(new Event("dashboardRefresh"));
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message || "Something went wrong",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="withdraw-page">
      <div className="withdraw-wrapper">
        <div className="withdraw-header">
          <h1>Withdraw</h1>
        </div>

        <div className="withdraw-card">
          <div className="withdraw-info-grid">
            <div className="withdraw-info-item">
              <span>Asset</span>
              <strong>{TOKEN}</strong>
            </div>

            <div className="withdraw-info-item">
              <span>Network</span>
              <strong>{NETWORK}</strong>
            </div>

            <div className="withdraw-info-item">
              <span>Minimum</span>
              <strong>${MIN_WITHDRAW}</strong>
            </div>

            <div className="withdraw-info-item">
              <span>Status</span>
              <strong>Manual Approval</strong>
            </div>
          </div>

          <div className="withdraw-fee-notice">
            <div className="withdraw-fee-notice-row">
              <span>Transaction Fee</span>
              <strong>{TRANSACTION_FEE_PERCENT}%</strong>
            </div>

            <div className="withdraw-fee-notice-row">
              <span>Early Withdraw Fee</span>
              <strong>
                {earlyWithdrawApplies ? `${EARLY_WITHDRAW_FEE_PERCENT}%` : "0%"}
              </strong>
            </div>

            <div className="withdraw-fee-notice-row">
              <span>Early Withdraw Rule</span>
              <strong>{EARLY_WITHDRAW_DAYS} Days</strong>
            </div>

            <div className="withdraw-fee-notice-row">
              <span>Current Total Fee</span>
              <strong>{totalFeePercent}%</strong>
            </div>
          </div>

          <form className="withdraw-form" onSubmit={handleSubmit}>
            <div className="withdraw-form-group">
              <label>Amount</label>
              <div className="withdraw-input-wrap">
                <input
                  type="number"
                  placeholder={`Minimum ${MIN_WITHDRAW}`}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
                <span>{TOKEN}</span>
              </div>
            </div>

            <div className="withdraw-form-group">
              <label>Wallet Address</label>
              <input
                type="text"
                placeholder="Enter TRC20 wallet address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="withdraw-calculator-box">
              <div className="withdraw-calculator-head">
                <h3>Withdraw Calculator</h3>
                <span>{loadingProfile ? "Checking..." : earlyWithdrawApplies ? "Early Fee Active" : "Normal Fee Active"}</span>
              </div>

              <div className="withdraw-calculator-row">
                <span>Requested Amount</span>
                <strong>${safeAmount.toFixed(2)}</strong>
              </div>

              <div className="withdraw-calculator-row">
                <span>Transaction Fee ({TRANSACTION_FEE_PERCENT}%)</span>
                <strong>-${transactionFee.toFixed(2)}</strong>
              </div>

              <div className="withdraw-calculator-row">
                <span>
                  Early Withdraw Fee ({earlyWithdrawApplies ? EARLY_WITHDRAW_FEE_PERCENT : 0}%)
                </span>
                <strong>-${earlyWithdrawFee.toFixed(2)}</strong>
              </div>

              <div className="withdraw-calculator-row">
                <span>Total Fee</span>
                <strong>-${totalFee.toFixed(2)}</strong>
              </div>

              <div className="withdraw-calculator-row withdraw-calculator-final">
                <span>Final Receivable</span>
                <strong>${receivableAmount.toFixed(2)}</strong>
              </div>
            </div>

            <button
              type="submit"
              className="withdraw-btn"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}